import React, { useState, useEffect } from 'react';
import {
  DollarSign, Users, TrendingUp, Package, Archive,
  Zap, Gift, CreditCard, AlertTriangle, BarChart3,
  RefreshCw, Database, Cloud, Clock
} from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';

import { BACKEND_URL } from '../config/api';

export default function AdminBilling() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricingCache, setPricingCache] = useState(null);
  const [pricingTiers, setPricingTiers] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin/billing-stats`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (res.ok) setStats(await res.clone().json());
      } catch (err) {
        console.error('Failed to fetch billing stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    fetchPricingCache();
    fetchPricingTiers();
  }, []);

  const fetchPricingCache = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/pricing/cache`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) setPricingCache(await res.json());
    } catch (err) {
      console.error('Failed to fetch pricing cache:', err);
    }
  };

  const fetchPricingTiers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/pricing/tiers`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) setPricingTiers(await res.json());
    } catch (err) {
      console.error('Failed to fetch pricing tiers:', err);
    }
  };

  const handleClearCache = async () => {
    setPricingLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/admin/pricing/cache/clear`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      await fetchPricingCache();
      await fetchPricingTiers();
    } catch (err) {
      console.error('Failed to clear cache:', err);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleRefreshCache = async () => {
    setPricingLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/admin/pricing/cache/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      await fetchPricingCache();
      await fetchPricingTiers();
    } catch (err) {
      console.error('Failed to refresh cache:', err);
    } finally {
      setPricingLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div data-testid="admin-billing">
          <h1 className="text-2xl font-medium text-charcoal mb-6">Billing Dashboard</h1>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-xl p-6 border border-beige animate-pulse h-28" />
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!stats) {
    return (
      <AdminLayout>
        <div data-testid="admin-billing">
          <h1 className="text-2xl font-medium text-charcoal mb-6">Billing Dashboard</h1>
          <p className="text-gray-soft">Failed to load billing stats.</p>
        </div>
      </AdminLayout>
    );
  }

  const { revenue, users_by_tier, users_by_status, upgrade_candidates, stashed_away, promos, transactions, cancelled_users } = stats;

  const tierColorMap = {
    free: 'bg-gray-100 text-gray-700',
    starter: 'bg-blue-100 text-blue-700',
    crafter: 'bg-green-100 text-green-700',
    artisan: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  const statusColorMap = {
    free: 'bg-gray-100 text-gray-700',
    active: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    suspended: 'bg-orange-100 text-orange-700',
  };

  const totalUsers = Object.values(users_by_tier).reduce((a, b) => a + b, 0);

  return (
    <AdminLayout>
      <div data-testid="admin-billing" className="space-y-6">
        <h1 className="text-2xl font-medium text-charcoal">Billing Dashboard</h1>

        {/* Revenue Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="MRR"
            value={`$${revenue.mrr.toFixed(2)}`}
            icon={DollarSign}
            color="text-green-600 bg-green-50"
            testId="mrr-card"
          />
          <StatCard
            label="ARPU"
            value={`$${revenue.arpu.toFixed(2)}`}
            icon={TrendingUp}
            color="text-blue-600 bg-blue-50"
            sub={`${revenue.paying_users} paying user${revenue.paying_users !== 1 ? 's' : ''}`}
            testId="arpu-card"
          />
          <StatCard
            label="Paid Items"
            value={revenue.total_paid_items.toLocaleString()}
            icon={Package}
            color="text-sage bg-sage-light"
            sub={`avg ${revenue.avg_paid_items_per_user}/user`}
            testId="paid-items-card"
          />
          <StatCard
            label="Cancelled"
            value={cancelled_users}
            icon={AlertTriangle}
            color="text-red-600 bg-red-50"
            testId="cancelled-card"
          />
        </div>

        {/* Revenue by Tier + Users by Tier */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Revenue by Tier */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-4 w-4 text-sage" />
                Revenue by Tier
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(revenue.revenue_by_tier).map(([tier, amount]) => {
                const pct = revenue.mrr > 0 ? (amount / revenue.mrr) * 100 : 0;
                return (
                  <div key={tier} data-testid={`revenue-tier-${tier}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize font-medium text-charcoal">{tier}</span>
                      <span className="text-charcoal">${amount.toFixed(2)}<span className="text-gray-soft text-xs ml-1">({pct.toFixed(0)}%)</span></span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
              <div className="pt-2 border-t border-beige flex justify-between text-sm font-medium">
                <span className="text-charcoal">Total MRR</span>
                <span className="text-charcoal">${revenue.mrr.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Users by Tier */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-sage" />
                Users by Tier
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(users_by_tier).sort((a, b) => b[1] - a[1]).map(([tier, count]) => {
                const pct = totalUsers > 0 ? (count / totalUsers) * 100 : 0;
                return (
                  <div key={tier} data-testid={`users-tier-${tier}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <div className="flex items-center gap-2">
                        <Badge className={tierColorMap[tier] || 'bg-gray-100 text-gray-700'}>
                          {tier}
                        </Badge>
                      </div>
                      <span className="text-charcoal">{count} <span className="text-gray-soft text-xs">({pct.toFixed(0)}%)</span></span>
                    </div>
                    <Progress value={pct} className="h-1.5" />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Billing Status + Transactions */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Billing Status Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4 text-sage" />
                Billing Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(users_by_status).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between p-3 bg-cream rounded-lg" data-testid={`status-${status}`}>
                    <Badge className={statusColorMap[status] || 'bg-gray-100 text-gray-700'}>
                      {status}
                    </Badge>
                    <span className="text-lg font-light text-charcoal">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Promo & Grant Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Gift className="h-4 w-4 text-sage" />
                Promos & Grants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Promo Codes" value={promos.total_codes} sub={`${promos.active_codes} active`} testId="promo-total" />
                <MiniStat label="Users w/ Promos" value={promos.users_with_promos} testId="promo-users" />
                <MiniStat label="Total Base Grants" value={promos.total_base_grant_items.toLocaleString()} sub="free items" testId="base-grants" />
                <MiniStat label="Transactions" value={transactions.total} sub={`${transactions.paid} paid`} testId="transactions" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stashed Away */}
        {stashed_away.total_items > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Archive className="h-4 w-4 text-amber-500" />
                Stashed Away
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                <div>
                  <div className="text-2xl font-light text-charcoal" data-testid="stashed-total">{stashed_away.total_items}</div>
                  <div className="text-xs text-gray-soft">Total items stashed</div>
                </div>
                <div>
                  <div className="text-2xl font-light text-charcoal" data-testid="stashed-users">{stashed_away.users_affected}</div>
                  <div className="text-xs text-gray-soft">Users affected</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upgrade Candidates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="h-4 w-4 text-amber-500" />
              Upgrade Candidates
              <span className="text-xs text-gray-soft font-normal ml-1">Users at 90%+ capacity</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upgrade_candidates.length === 0 ? (
              <p className="text-sm text-gray-soft">No users near their limit right now.</p>
            ) : (
              <div className="space-y-3">
                {upgrade_candidates.map((u) => (
                  <div
                    key={u.user_id}
                    className="p-3 bg-cream rounded-lg"
                    data-testid={`candidate-${u.user_id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-charcoal text-sm truncate">{u.name || u.email}</div>
                        <div className="text-xs text-gray-soft truncate">{u.email}</div>
                      </div>
                      <Badge className={`ml-2 flex-shrink-0 ${tierColorMap[u.tier] || 'bg-gray-100'}`}>
                        {u.tier}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress
                        value={u.usage_percent}
                        className={`h-2 flex-1 ${u.usage_percent >= 100 ? '[&>div]:bg-red-500' : '[&>div]:bg-amber-500'}`}
                      />
                      <span className={`text-sm font-medium flex-shrink-0 ${u.usage_percent >= 100 ? 'text-red-600' : 'text-amber-600'}`}>
                        {u.usage_percent}%
                      </span>
                    </div>
                    <div className="flex gap-4 mt-1.5 text-xs text-gray-soft">
                      <span>{u.stash_item_count}/{u.stash_item_limit} items</span>
                      <span>{u.paid_item_slots} paid slots</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stripe Pricing Cache Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4 text-purple-500" />
              Stripe Pricing Cache
              <span className="text-xs text-gray-soft font-normal ml-1">Manage pricing data source</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pricingCache ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-cream rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      {pricingCache.source === 'stripe' ? (
                        <Cloud className="h-4 w-4 text-green-600" />
                      ) : (
                        <Database className="h-4 w-4 text-amber-600" />
                      )}
                      <span className="text-xs text-gray-soft">Source</span>
                    </div>
                    <div className="text-sm font-medium text-charcoal capitalize">
                      {pricingCache.source || 'None'}
                    </div>
                  </div>
                  <div className="p-3 bg-cream rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="h-4 w-4 text-gray-soft" />
                      <span className="text-xs text-gray-soft">Cache Age</span>
                    </div>
                    <div className="text-sm font-medium text-charcoal">
                      {pricingCache.cache_age_seconds !== null 
                        ? `${Math.floor(pricingCache.cache_age_seconds / 60)}m ${pricingCache.cache_age_seconds % 60}s`
                        : 'Empty'}
                    </div>
                  </div>
                  <div className="p-3 bg-cream rounded-lg">
                    <div className="text-xs text-gray-soft mb-1">Tiers Loaded</div>
                    <div className="text-sm font-medium text-charcoal">
                      {pricingCache.tier_count || 0}
                    </div>
                  </div>
                  <div className="p-3 bg-cream rounded-lg">
                    <div className="text-xs text-gray-soft mb-1">Status</div>
                    <Badge className={pricingCache.is_stale ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                      {pricingCache.is_stale ? 'Stale' : 'Fresh'}
                    </Badge>
                  </div>
                </div>

                {pricingCache.price_id && (
                  <div className="text-xs text-gray-soft">
                    Price ID: <code className="bg-cream px-1 py-0.5 rounded">{pricingCache.price_id}</code>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshCache}
                    disabled={pricingLoading}
                    data-testid="refresh-pricing-cache"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${pricingLoading ? 'animate-spin' : ''}`} />
                    Refresh from Stripe
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearCache}
                    disabled={pricingLoading}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="clear-pricing-cache"
                  >
                    Clear Cache
                  </Button>
                </div>

                <p className="text-xs text-gray-soft">
                  Cache TTL: {Math.floor(pricingCache.cache_ttl_seconds / 60)} minutes. 
                  Pricing is fetched from Stripe and cached locally. Clear cache to force a fresh fetch on next request.
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-soft">Loading cache status...</p>
            )}
          </CardContent>
        </Card>

        {/* Pricing Tiers Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4 text-green-600" />
              Pricing Tiers
              <span className="text-xs text-gray-soft font-normal ml-1">
                {pricingTiers?.source === 'stripe' ? 'From Stripe' : 'Fallback defaults'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pricingTiers?.tiers ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-beige">
                      <th className="text-left py-2 px-3 font-medium text-charcoal">Tier</th>
                      <th className="text-left py-2 px-3 font-medium text-charcoal">Items Range</th>
                      <th className="text-right py-2 px-3 font-medium text-charcoal">Flat Fee</th>
                      <th className="text-right py-2 px-3 font-medium text-charcoal">Per Item</th>
                      <th className="text-right py-2 px-3 font-medium text-charcoal">Example Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Free tier row */}
                    <tr className="border-b border-beige/50 bg-gray-50">
                      <td className="py-2 px-3">
                        <Badge className="bg-gray-100 text-gray-700">Free</Badge>
                      </td>
                      <td className="py-2 px-3 text-gray-soft">0 paid items</td>
                      <td className="py-2 px-3 text-right text-gray-soft">$0.00</td>
                      <td className="py-2 px-3 text-right text-gray-soft">—</td>
                      <td className="py-2 px-3 text-right font-medium text-charcoal">$0.00/mo</td>
                    </tr>
                    {pricingTiers.tiers.map((tier, idx) => {
                      const tierColors = {
                        starter: 'bg-blue-100 text-blue-700',
                        crafter: 'bg-green-100 text-green-700',
                        artisan: 'bg-amber-100 text-amber-700',
                      };
                      const maxDisplay = tier.max_items >= 999999 ? '∞' : tier.max_items;
                      
                      // Minimum paid items is 100, so first tier displays as "100" not "1-100"
                      const minDisplay = tier.min_items === 1 ? 100 : tier.min_items;
                      const rangeDisplay = minDisplay === tier.max_items || (tier.flat_amount > 0 && tier.max_items === 100)
                        ? `${minDisplay}` 
                        : `${minDisplay} – ${maxDisplay}`;
                      
                      // Example calculation
                      const exampleQty = minDisplay;
                      const exampleCost = tier.flat_amount > 0 
                        ? tier.flat_amount 
                        : (tier.unit_amount * exampleQty).toFixed(2);
                      
                      return (
                        <tr key={idx} className="border-b border-beige/50 hover:bg-cream/50">
                          <td className="py-2 px-3">
                            <Badge className={tierColors[tier.tier] || 'bg-gray-100 text-gray-700'}>
                              {tier.name}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-gray-soft">
                            {rangeDisplay}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-soft">
                            {tier.flat_amount > 0 ? `$${tier.flat_amount.toFixed(2)}` : '—'}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-soft">
                            {tier.unit_amount > 0 ? `$${tier.unit_amount.toFixed(5)}` : '—'}
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-charcoal">
                            ${exampleCost}/mo
                            <span className="text-xs text-gray-soft ml-1">({exampleQty} items)</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-xs text-gray-soft mt-3">
                  Pricing mode: <code className="bg-cream px-1 py-0.5 rounded">{pricingTiers.tiers_mode || 'volume'}</code> — 
                  Item pricing is applied based on total paid quantity
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-soft">Loading pricing tiers...</p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function StatCard({ label, value, icon: Icon, color, sub, testId }) {
  return (
    <div className="bg-white rounded-xl p-4 border border-beige" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="text-xs text-gray-soft">{label}</div>
          <div className="text-xl font-medium text-charcoal">{value}</div>
          {sub && <div className="text-xs text-gray-soft">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, testId }) {
  return (
    <div className="p-3 bg-cream rounded-lg" data-testid={testId}>
      <div className="text-lg font-light text-charcoal">{value}</div>
      <div className="text-xs text-gray-soft">{label}</div>
      {sub && <div className="text-xs text-sage">{sub}</div>}
    </div>
  );
}
